import { useState, useEffect, useCallback } from 'react';
import { PrimesData, StatusType, GradeData, Profession } from '../types';
import {
  calculateTotalMonthlyPrimes,
  calculatePrimeNuitAmount,
  calculateSFTAmount,
  calculateISSAmount,
  calculateNBIAmount,
  calculateInsalubriteCat2Amount
} from '../utils/primeCalculations';
import { validatePrimeAmount, validateReimbursementRate } from '../utils/validation';
import { usePrimeCalculations } from './usePrimeCalculations';
import { usePrimeValidation } from './usePrimeValidation';
import { DEFAULT_PRIMES } from '../constants/primes';

interface UsePrimesManagementProps {
  selectedGrade: number | null;
  selectedEchelon: number | null;
  workTimePercentage: number;
  primesData: PrimesData;
  currentProfessionId: string;
  selectedStatus: StatusType;
  isLoading: boolean;
  grades: GradeData[];
  selectedProfession: Profession;
  onPrimesChange: (primesData: PrimesData) => void;
  onMarkPrimesModified: () => void;
}

export function usePrimesManagement({
  selectedGrade,
  selectedEchelon,
  workTimePercentage,
  primesData,
  currentProfessionId,
  selectedStatus,
  isLoading,
  grades,
  selectedProfession,
  onPrimesChange,
  onMarkPrimesModified
}: UsePrimesManagementProps) {
  // États locaux
  const [editingPrime, setEditingPrime] = useState<string | null>(null);
  const [editingBaseAmount, setEditingBaseAmount] = useState<string | null>(null);
  const [editingReimbursementRate, setEditingReimbursementRate] = useState<string | null>(null);
  const [expandedPrimes, setExpandedPrimes] = useState<Set<string>>(new Set());
  const [showSuccessMessage, setShowSuccessMessage] = useState<string | null>(null);
  const [selectedZoneIR, setSelectedZoneIR] = useState<number | null>(null);
  const [expandedCalculationDetails, setExpandedCalculationDetails] = useState<Set<string>>(new Set());

  // Utilisation des hooks existants
  const { 
    currentIndiceMajore, 
    primesWithCalculatedAmounts,
    isCalculatingPrimes
  } = usePrimeCalculations({
    selectedGrade,
    selectedEchelon,
    selectedZoneIR,
    primesData: primesData.primes.length > 0 ? primesData : { primes: DEFAULT_PRIMES, totalMensuel: 0 },
    currentProfessionId,
    selectedStatus,
    isLoading
  });

  const {
    validationErrors,
    gradeEchelonValidation,
    setValidationErrors
  } = usePrimeValidation(selectedGrade, selectedEchelon, grades, selectedProfession);

  // Clear success message after delay
  useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => setShowSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage]);

  // Update data when calculated amounts change
  useEffect(() => {
    if (isLoading || primesWithCalculatedAmounts.length === 0) return;
    
    // Only update if there's a meaningful change to prevent infinite loops
    const currentTotal = calculateTotalMonthlyPrimes(primesWithCalculatedAmounts, workTimePercentage);
    const hasSignificantChange = Math.abs(primesData.totalMensuel - currentTotal) > 0.01 ||
      primesData.primes.length !== primesWithCalculatedAmounts.length;
    
    if (!hasSignificantChange) return;
    
    const newPrimesData = {
      primes: primesWithCalculatedAmounts,
      totalMensuel: currentTotal
    };
    
    onPrimesChange(newPrimesData);
  }, [primesWithCalculatedAmounts, workTimePercentage, onPrimesChange, primesData.totalMensuel, primesData.primes.length]);

  // Gestionnaires d'événements
  const handleTogglePrime = useCallback((primeId: string) => {
    const prime = primesWithCalculatedAmounts.find(p => p.id === primeId);
    if (prime && !prime.active) {
      setExpandedPrimes(prev => new Set(prev).add(primeId));
    }
    
    const updatedPrimes = primesWithCalculatedAmounts.map(p => 
      p.id === primeId ? { ...p, active: !p.active } : p
    );
    
    const newPrimesData = {
      primes: updatedPrimes,
      totalMensuel: calculateTotalMonthlyPrimes(updatedPrimes, workTimePercentage)
    };
    
    onPrimesChange(newPrimesData);
    onMarkPrimesModified();
  }, [primesWithCalculatedAmounts, workTimePercentage, onPrimesChange, onMarkPrimesModified]);

  const handleToggleDetails = useCallback((primeId: string) => {
    setExpandedPrimes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(primeId)) {
        newSet.delete(primeId);
      } else {
        newSet.add(primeId);
      }
      return newSet;
    });
  }, []);

  const handleToggleCalculationDetails = useCallback((primeId: string) => {
    setExpandedCalculationDetails(prev => {
      const newSet = new Set(prev);
      if (newSet.has(primeId)) {
        newSet.delete(primeId);
      } else {
        newSet.add(primeId);
      }
      return newSet;
    });
  }, []);

  const handleEditAmount = useCallback((primeId: string) => {
    setEditingPrime(primeId);
  }, []);

  const handleSaveAmount = useCallback((primeId: string, amount: number): boolean => {
    const validation = validatePrimeAmount(amount, 'montant de la prime');
    
    if (validation.isValid) {
      // Clear any existing errors
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[primeId];
        return newErrors;
      });
      
      const updatedPrimes = primesWithCalculatedAmounts.map(p => 
        p.id === primeId ? { ...p, montantUtilisateur: amount } : p
      );
      
      const newPrimesData = {
        primes: updatedPrimes,
        totalMensuel: calculateTotalMonthlyPrimes(updatedPrimes, workTimePercentage)
      };
      
      onPrimesChange(newPrimesData);
      onMarkPrimesModified();
      setEditingPrime(null);
      setShowSuccessMessage(`Prime "${primesWithCalculatedAmounts.find(p => p.id === primeId)?.nom}" mise à jour`);
      return true;
    } else {
      // Set validation errors
      setValidationErrors(prev => ({
        ...prev,
        [primeId]: validation.errors
      }));
      return false;
    }
  }, [primesWithCalculatedAmounts, workTimePercentage, onPrimesChange, onMarkPrimesModified, setValidationErrors]);

  const handleCancelEditAmount = useCallback(() => {
    setEditingPrime(null);
  }, []);

  const handleEditBaseAmount = useCallback((primeId: string) => {
    setEditingBaseAmount(primeId);
  }, []);

  const handleSaveBaseAmount = useCallback((primeId: string, baseAmount: number): boolean => {
    const validation = validatePrimeAmount(baseAmount, 'montant de base');
    
    if (validation.isValid) {
      // Clear any existing errors
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${primeId}_base`];
        return newErrors;
      });
      
      const prime = primesWithCalculatedAmounts.find(p => p.id === primeId);
      if (prime?.pourcentageRemboursement) {
        const newAmount = (baseAmount * prime.pourcentageRemboursement) / 100;
        const updatedPrimes = primesWithCalculatedAmounts.map(p => 
          p.id === primeId ? { 
            ...p, 
            montantBase: baseAmount,
            montantUtilisateur: newAmount 
          } : p
        );
        
        const newPrimesData = {
          primes: updatedPrimes,
          totalMensuel: calculateTotalMonthlyPrimes(updatedPrimes, workTimePercentage)
        };
        
        onPrimesChange(newPrimesData);
        onMarkPrimesModified();
      }
      setEditingBaseAmount(null);
      setShowSuccessMessage(`Montant de base mis à jour`);
      return true;
    } else {
      // Set validation errors
      setValidationErrors(prev => ({
        ...prev,
        [`${primeId}_base`]: validation.errors
      }));
      return false;
    }
  }, [primesWithCalculatedAmounts, workTimePercentage, onPrimesChange, onMarkPrimesModified, setValidationErrors]);

  const handleCancelEditBaseAmount = useCallback(() => {
    setEditingBaseAmount(null);
  }, []);

  const handleEditReimbursementRate = useCallback((primeId: string) => {
    setEditingReimbursementRate(primeId);
  }, []);

  const handleSaveReimbursementRate = useCallback((primeId: string, rate: number): boolean => {
    const validation = validateReimbursementRate(rate);
    
    if (validation.isValid) {
      // Clear any existing errors
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${primeId}_rate`];
        return newErrors;
      });
      
      const prime = primesWithCalculatedAmounts.find(p => p.id === primeId);
      if (prime?.montantBase) {
        const newAmount = (prime.montantBase * rate) / 100;
        const updatedPrimes = primesWithCalculatedAmounts.map(p => 
          p.id === primeId ? { 
            ...p, 
            pourcentageRemboursement: rate,
            montantUtilisateur: newAmount 
          } : p
        );
        
        const newPrimesData = {
          primes: updatedPrimes,
          totalMensuel: calculateTotalMonthlyPrimes(updatedPrimes, workTimePercentage)
        };
        
        onPrimesChange(newPrimesData);
        onMarkPrimesModified();
      }
      setEditingReimbursementRate(null);
      setShowSuccessMessage(`Taux de remboursement mis à jour`);
      return true;
    } else {
      // Set validation errors
      setValidationErrors(prev => ({
        ...prev,
        [`${primeId}_rate`]: validation.errors
      }));
      return false;
    }
  }, [primesWithCalculatedAmounts, workTimePercentage, onPrimesChange, onMarkPrimesModified, setValidationErrors]);

  const handleCancelEditReimbursementRate = useCallback(() => {
    setEditingReimbursementRate(null);
  }, []);

  const handleIFTDConfigChange = useCallback((primeId: string, config: { dimanchesParMois?: number; joursFeriesTravailles?: string[] }) => {
    const updatedPrimes = primesWithCalculatedAmounts.map(p => {
      if (p.id === primeId) {
        const newConfig = { ...p.iftdConfig, ...config };
        const dimanchesParMois = newConfig.dimanchesParMois || 0;
        const joursFeriesTravailles = newConfig.joursFeriesTravailles || [];
        
        // Calcul IFTD : 60€ × nb_dimanches + (60€ × nb_jours_fériés / 12)
        const montantDimanches = 60 * dimanchesParMois;
        const montantJoursFeries = (60 * joursFeriesTravailles.length) / 12;
        const montantTotal = montantDimanches + montantJoursFeries;
        
        return {
          ...p,
          iftdConfig: newConfig,
          montantUtilisateur: Math.round(montantTotal * 100) / 100
        };
      }
      return p;
    });
    
    const newPrimesData = {
      primes: updatedPrimes,
      totalMensuel: calculateTotalMonthlyPrimes(updatedPrimes, workTimePercentage)
    };
    
    onPrimesChange(newPrimesData);
    onMarkPrimesModified();
  }, [primesWithCalculatedAmounts, workTimePercentage, onPrimesChange, onMarkPrimesModified]);

  const handleContagionConfigChange = useCallback((primeId: string, config: { joursTravaillesParAn?: number }) => {
    const updatedPrimes = primesWithCalculatedAmounts.map(p => {
      if (p.id === primeId) {
        const newConfig = { ...p.contagionConfig, ...config };
        const joursTravaillesParAn = newConfig.joursTravaillesParAn || 228;
        
        // Calcul prime de contagion : 0,33€ × 2 × nb_jours_travaillés / 12
        const montantMensuel = (0.33 * 2 * joursTravaillesParAn) / 12;
        
        return {
          ...p,
          contagionConfig: newConfig,
          montantUtilisateur: Math.round(montantMensuel * 100) / 100
        };
      }
      return p;
    });
    
    const newPrimesData = {
      primes: updatedPrimes,
      totalMensuel: calculateTotalMonthlyPrimes(updatedPrimes, workTimePercentage)
    };
    
    onPrimesChange(newPrimesData);
    onMarkPrimesModified();
  }, [primesWithCalculatedAmounts, workTimePercentage, onPrimesChange, onMarkPrimesModified]);

  const handlePrimeNuitConfigChange = useCallback((primeId: string, config: { nuitsParMois?: number }) => {
    const updatedPrimes = primesWithCalculatedAmounts.map(p => {
      if (p.id === primeId) {
        const newConfig = { ...p.primeNuitConfig, ...config };
        const nuitsParMois = newConfig.nuitsParMois || 0;

        const montantMensuel = currentIndiceMajore && nuitsParMois > 0
          ? calculatePrimeNuitAmount(currentIndiceMajore, nuitsParMois)
          : 0;

        return {
          ...p,
          primeNuitConfig: newConfig,
          montantUtilisateur: montantMensuel
        };
      }
      return p;
    });
    
    const newPrimesData = {
      primes: updatedPrimes,
      totalMensuel: calculateTotalMonthlyPrimes(updatedPrimes, workTimePercentage)
    };
    
    onPrimesChange(newPrimesData);
    onMarkPrimesModified();
  }, [primesWithCalculatedAmounts, workTimePercentage, currentIndiceMajore, onPrimesChange, onMarkPrimesModified]);

  const handleSFTConfigChange = useCallback((primeId: string, config: { nombreEnfants?: number }) => {
    const updatedPrimes = primesWithCalculatedAmounts.map(p => {
      if (p.id === primeId) {
        const newConfig = { ...p.sftConfig, ...config };
        const nombreEnfants = newConfig.nombreEnfants || 0;

        const montantMensuel = currentIndiceMajore && nombreEnfants > 0
          ? calculateSFTAmount(currentIndiceMajore, nombreEnfants)
          : 0;

        return {
          ...p,
          sftConfig: newConfig,
          montantUtilisateur: montantMensuel
        };
      }
      return p;
    });
    
    const newPrimesData = {
      primes: updatedPrimes,
      totalMensuel: calculateTotalMonthlyPrimes(updatedPrimes, workTimePercentage)
    };
    
    onPrimesChange(newPrimesData);
    onMarkPrimesModified();
  }, [primesWithCalculatedAmounts, workTimePercentage, currentIndiceMajore, onPrimesChange, onMarkPrimesModified]);

  const handleISSConfigChange = useCallback((primeId: string, config: { echelonHistorique?: number; indiceMajoreHistorique?: number }) => {
    const updatedPrimes = primesWithCalculatedAmounts.map(p => {
      if (p.id === primeId) {
        const newConfig = { ...p.issConfig, ...config };

        const montantMensuel = newConfig.indiceMajoreHistorique && newConfig.indiceMajoreHistorique > 0
          ? calculateISSAmount(newConfig.indiceMajoreHistorique)
          : 0;

        return {
          ...p,
          issConfig: newConfig,
          montantUtilisateur: montantMensuel
        };
      }
      return p;
    });

    const newPrimesData = {
      primes: updatedPrimes,
      totalMensuel: calculateTotalMonthlyPrimes(updatedPrimes, workTimePercentage)
    };

    onPrimesChange(newPrimesData);
    onMarkPrimesModified();
  }, [primesWithCalculatedAmounts, workTimePercentage, onPrimesChange, onMarkPrimesModified]);

  const handleNBIConfigChange = useCallback((primeId: string, config: { points?: number }) => {
    const updatedPrimes = primesWithCalculatedAmounts.map(p => {
      if (p.id === primeId) {
        const newConfig = { ...p.nbiConfig, ...config };
        const points = newConfig.points || 15;

        return {
          ...p,
          nbiConfig: newConfig,
          montantUtilisateur: calculateNBIAmount(points)
        };
      }
      return p;
    });
    
    const newPrimesData = {
      primes: updatedPrimes,
      totalMensuel: calculateTotalMonthlyPrimes(updatedPrimes, workTimePercentage)
    };
    
    onPrimesChange(newPrimesData);
    onMarkPrimesModified();
  }, [primesWithCalculatedAmounts, workTimePercentage, onPrimesChange, onMarkPrimesModified]);

  const handleInsalubriteCat2ConfigChange = useCallback((primeId: string, config: { joursTravaillesParMois?: number }) => {
    const updatedPrimes = primesWithCalculatedAmounts.map(p => {
      if (p.id === primeId) {
        const newConfig = { ...p.insalubriteCat2Config, ...config };
        const joursTravaillesParMois = newConfig.joursTravaillesParMois || 22;
        const montantDemiJournee = p.montantDemiJourneeInsalubrite || 0.31;

        return {
          ...p,
          insalubriteCat2Config: newConfig,
          montantUtilisateur: calculateInsalubriteCat2Amount(montantDemiJournee, joursTravaillesParMois)
        };
      }
      return p;
    });

    const newPrimesData = {
      primes: updatedPrimes,
      totalMensuel: calculateTotalMonthlyPrimes(updatedPrimes, workTimePercentage)
    };

    onPrimesChange(newPrimesData);
    onMarkPrimesModified();
  }, [primesWithCalculatedAmounts, workTimePercentage, onPrimesChange, onMarkPrimesModified]);

  const handleTransportBaseAmountChange = useCallback((primeId: string, newBaseAmount: number) => {
    const updatedPrimes = primesWithCalculatedAmounts.map(p => {
      if (p.id === primeId && p.id === 'transport') {
        const pourcentageRemboursement = p.pourcentageRemboursement || 75;
        const newMontantUtilisateur = (newBaseAmount * pourcentageRemboursement) / 100;
        
        return {
          ...p,
          montantBase: newBaseAmount,
          montantUtilisateur: newMontantUtilisateur
        };
      }
      return p;
    });
    
    const newPrimesData = {
      primes: updatedPrimes,
      totalMensuel: calculateTotalMonthlyPrimes(updatedPrimes, workTimePercentage)
    };
    
    onPrimesChange(newPrimesData);
    onMarkPrimesModified();
  }, [primesWithCalculatedAmounts, workTimePercentage, onPrimesChange, onMarkPrimesModified]);

  const handleZoneChange = useCallback((zone: number) => {
    setSelectedZoneIR(zone);
  }, []);

  const getDocumentationUrl = useCallback((primeId: string): string | undefined => {
    const prime = primesWithCalculatedAmounts.find(p => p.id === primeId);
    return prime?.urlDoc;
  }, [primesWithCalculatedAmounts]);

  return {
    // États
    editingPrime,
    editingBaseAmount,
    editingReimbursementRate,
    expandedPrimes,
    showSuccessMessage,
    selectedZoneIR,
    expandedCalculationDetails,
    
    // Données calculées
    currentIndiceMajore,
    primesWithCalculatedAmounts,
    isCalculatingPrimes,
    validationErrors,
    gradeEchelonValidation,
    
    // Gestionnaires d'événements
    handleTogglePrime,
    handleToggleDetails,
    handleToggleCalculationDetails,
    handleEditAmount,
    handleSaveAmount,
    handleCancelEditAmount,
    handleEditBaseAmount,
    handleSaveBaseAmount,
    handleCancelEditBaseAmount,
    handleEditReimbursementRate,
    handleSaveReimbursementRate,
    handleCancelEditReimbursementRate,
    handleIFTDConfigChange,
    handleContagionConfigChange,
    handlePrimeNuitConfigChange,
    handleSFTConfigChange,
    handleInsalubriteCat2ConfigChange,
    handleISSConfigChange,
    handleNBIConfigChange,
    handleTransportBaseAmountChange,
    handleZoneChange,
    getDocumentationUrl
  };
}